import React, { useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { isMultiOperator, isNoValueOperator } from "../../../../../../common/FiltersFormEngine/operatorRules";
import {
	DashboardFilterInstance,
	FilterDefinition,
	FilterOperator,
	FilterOption,
} from "../../../../../../context/FiltersProvider/types";

type Props = {
	filter: FilterDefinition;
	instance: DashboardFilterInstance;

	onUpdate: (instanceId: string, patch: Partial<DashboardFilterInstance>) => void;
};

export const DashboardFilterDropdown = ({ filter, instance, onUpdate }: Props) => {
	const { t } = useTranslation("common");

	const [search, setSearch] = useState("");

	// Field config
	const field = filter.field;

	const operators = useMemo(() => {
		return field?.operators ?? [];
	}, [field?.operators]);

	const options = useMemo(() => {
		return field?.options ?? [];
	}, [field?.options]);

	const filteredOptions = useMemo(() => {
		const query = search.trim().toLowerCase();

		if (!query.length) {
			return options;
		}

		return options.filter((option) => option.label.toLowerCase().includes(query));
	}, [options, search]);

	if (!field) {
		return null;
	}

	// Usefull flags
	const supportsMultiSelect = isMultiOperator(instance.operator as FilterOperator);
	const isNoValue = isNoValueOperator(instance.operator as FilterOperator);
	const requiresValue = !isNoValue;
	const isTextInput = field.inputType === "text";

	// Helpers functions
	const getArrayValue = (): Array<string> => {
		if (!Array.isArray(instance.value)) {
			return [];
		}

		return instance.value;
	};

	const isSelected = (optionId: string) => {
		// multi
		if (Array.isArray(instance.value)) {
			return instance.value.includes(optionId);
		}

		// single
		return instance.value === optionId;
	};

	// handle change operator
	const handleOperatorChange = (event: SelectChangeEvent<string>) => {
		const operator = event.target.value as FilterOperator;

		const isMulti = isMultiOperator(operator);
		const wasMulti = isMultiOperator(instance.operator as FilterOperator);

		const isNoValue = isNoValueOperator(operator);

		onUpdate(instance.id, {
			operator,

			value: isNoValue ? undefined : isMulti === wasMulti ? instance.value : undefined,
		});
	};

	// Handle select option
	const handleOptionToggle = (option: FilterOption) => {
		// multi
		if (supportsMultiSelect) {
			const currentValues = getArrayValue();

			const alreadySelected = currentValues.includes(option.id);

			const nextValues = alreadySelected
				? currentValues.filter((value) => value !== option.id)
				: [...currentValues, option.id];

			onUpdate(instance.id, {
				value: nextValues,
			});

			return;
		}

		// single
		onUpdate(instance.id, {
			value: option.id,
			open: false,
		});
	};

	// Handle select all
	const handleSelectAll = () => {
		if (!supportsMultiSelect) {
			return;
		}

		const allValues = filteredOptions.map((option) => option.id);

		onUpdate(instance.id, {
			value: allValues,
		});
	};

	// Clear all
	const handleClearAll = () => {
		onUpdate(instance.id, {
			value: supportsMultiSelect ? [] : undefined,
		});
	};

	return (
		<Paper
			elevation={4}
			sx={{
				position: "absolute",
				top: "calc(100% + 4px)",
				left: 0,
				zIndex: 10000,

				width: 340,
				maxWidth: 400,

				display: "flex",
				flexDirection: "column",
				gap: 1.5,

				p: 2,

				borderRadius: 2,
			}}
		>
			{/* operator */}
			<Stack direction="row" spacing={2}>
				<FormControl fullWidth size="small">
					<InputLabel>{t("operator")}</InputLabel>

					<Select
						size="small"
						value={instance.operator ?? ""}
						label={t("operator")}
						onChange={handleOperatorChange}
						MenuProps={{
							sx: {
								zIndex: 11000,
							},
						}}
					>
						{operators.map((operator) => (
							<MenuItem key={operator.operator} value={operator.operator}>
								{operator.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{/* search */}
				{requiresValue && options.length > 0 && (
					<TextField
						size="small"
						placeholder={t("search")}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				)}
			</Stack>

			{!isNoValue && isTextInput && (
				<TextField
					fullWidth
					size="small"
					value={typeof instance.value === "string" ? instance.value : ""}
					placeholder={t("value")}
					onChange={(e) => {
						onUpdate(instance.id, {
							value: e.target.value,
						});
					}}
					sx={{
						"& input": {
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						},
					}}
				/>
			)}

			{/* value section */}
			{requiresValue && !isTextInput && (
				<>
					{/* options */}
					<Box
						sx={{
							maxHeight: 240,
							overflowY: "auto",
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
						}}
					>
						{filteredOptions.length === 0 && (
							<Typography
								variant="body2"
								sx={{
									p: 2,
								}}
							>
								{t("no filters found")}
							</Typography>
						)}

						{filteredOptions.map((option) => {
							const selected = isSelected(option.id);

							return (
								<MenuItem
									key={option.id}
									selected={selected}
									onClick={() => handleOptionToggle(option)}
								>
									{supportsMultiSelect && (
										<Checkbox sx={{ width: 20, height: 20, marginRight: 2 }} checked={selected} />
									)}

									{option.label}
								</MenuItem>
							);
						})}
					</Box>

					{/* multi actions */}
					{supportsMultiSelect && filteredOptions.length > 0 && (
						<Stack direction="row" spacing={2} width={"100%"} justifyContent={"end"}>
							<Button variant="outlined" size="small" onClick={handleSelectAll}>
								{t("select all")}
							</Button>

							<Button variant="outlined" size="small" onClick={handleClearAll}>
								{t("clear")}
							</Button>
						</Stack>
					)}
				</>
			)}
		</Paper>
	);
};
